import React, { useEffect, useState, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const AffordCalculator = () => {
  const [grossIncome, setGrossIncome] = useState(75000);
  const [monthlyDebt, setMonthlyDebt] = useState(300);
  const [downPayment, setDownPayment] = useState(50000);
  const [loanTerm, setLoanTerm] = useState(360);
  const [interestRate, setInterestRate] = useState(6.5);
  const [insurance, setInsurance] = useState(0);
  const [propertyTax, setPropertyTax] = useState(0);
  const [assocFee, setAssocFee] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [homePrice, setHomePrice] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);

  const calculateCurrentMonthlyPaymentPlan = useCallback(
    (interestRate, loanTerm) => {
      const monthlyIncome = grossIncome / 12;
      const maxMonthlyPayment = monthlyIncome * 0.28 - monthlyDebt;

      const monthlyInterestRate = interestRate / 100 / 12;
      const n = loanTerm;

      const estimatedLoanAmount =
        maxMonthlyPayment /
        (monthlyInterestRate / (1 - Math.pow(1 + monthlyInterestRate, -n)));

      let actualLoanAmount = estimatedLoanAmount;
      let actualDownPayment = downPayment;

      const actualHomePrice = actualLoanAmount + actualDownPayment;
      return {
        homePrice: actualHomePrice,
        loanAmount: actualLoanAmount,
        downPayment: actualDownPayment,
      };
    },
    [grossIncome, monthlyDebt, downPayment]
  );

  const calculateEMI = useCallback(() => {
    const {
      homePrice: hp,
      loanAmount: la,
      downPayment: dp,
    } = calculateCurrentMonthlyPaymentPlan(interestRate, loanTerm);

    setHomePrice(hp);
    setLoanAmount(la);
    setDownPayment(dp);

    const monthlyInterestRate = interestRate / 100 / 12;
    const n = loanTerm;

    const monthlyEMI =
      la * (monthlyInterestRate / (1 - Math.pow(1 + monthlyInterestRate, -n)));
    const totalMonthlyPayment =
      monthlyEMI + propertyTax + +insurance + +assocFee;

    setMonthlyPayment(Math.max(0, totalMonthlyPayment.toFixed(2)));
  }, [
    interestRate,
    loanTerm,
    propertyTax,
    insurance,
    assocFee,
    calculateCurrentMonthlyPaymentPlan,
  ]);

  useEffect(() => {
    calculateEMI();
  }, [calculateEMI]);

  const data = [
    { name: "Loan Amount", value: loanAmount },
    { name: "Downpayment", value: downPayment },
    { name: "Monthly Payment", value: +monthlyPayment },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isSmallScreen = viewportWidth <= 500;
  const pieChartWidth = isSmallScreen ? 300 : 400;
  const pieChartHeight = isSmallScreen ? 350 : 420;
  const innerRadius = isSmallScreen ? 110 : 130;
  const outerRadius = isSmallScreen ? 150 : 180;

  return (
    <div className="mortgageCalculatorParent">
      <div className="mortgageCalculator">
        <div className="mortgageCalculatorHeaderContent">
          <span className="mortgageCalculatorHeader">
            Mortgage Affordability & Refinance Tool
          </span>
          <span className="mortgageCalculatorContent">
            Determine the house price range you can afford and qualify for with
            this calculator.
          </span>
        </div>
        <div className="mortgageCalculatorBox">
          <div className="leftSideBox">
            <label>
              Gross annual income: <br />
              <input
                type="text"
                value={`$ ${Number(grossIncome).toLocaleString()}`}
                onChange={(e) =>
                  setGrossIncome(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
              <input
                type="range"
                min="0"
                max="1000000"
                value={grossIncome}
                onChange={(e) => setGrossIncome(+e.target.value)}
              />
            </label>

            <label>
              Monthly debt payments: <br />
              <input
                type="text"
                value={`$ ${Number(monthlyDebt).toLocaleString()}`}
                onChange={(e) =>
                  setMonthlyDebt(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
              <input
                type="range"
                min="0"
                max="50000"
                step="100"
                value={monthlyDebt}
                onChange={(e) => setMonthlyDebt(+e.target.value)}
              />
            </label>

            <label>
              Down payment: <br />
              <input
                type="text"
                value={`$ ${Number(downPayment).toLocaleString()}`}
                onChange={(e) =>
                  setDownPayment(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
              <input
                type="range"
                min="0"
                max="1000000"
                value={downPayment}
                onChange={(e) => setDownPayment(+e.target.value)}
              />
            </label>

            <label>
              Annual interest rate (%): <br />
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  min="0"
                  max="100"
                  step="0.001"
                  value={interestRate}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9.]/g, "");

                    // Allow only one decimal point
                    if ((value.match(/\./g) || []).length > 1) {
                      value = value.slice(0, -1);
                    }

                    // Ensure there are no more than two digits after the decimal point
                    const [integer, decimal] = value.split(".");
                    if (decimal && decimal.length > 2) {
                      value = `${integer}.${decimal.slice(0, 3)}`; // Limit to two decimal places
                    }

                    // Ensure the value is between 0 and 100
                    if (+value < 0) value = "0";
                    if (+value > 100) value = "100";

                    setInterestRate(value);
                  }}
                  style={{ width: "100%" }} // Adjust width as needed
                />
                <span style={{ marginLeft: "-30px", fontWeight: "bold" }}>
                  %
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={interestRate}
                step="0.01"
                onChange={(e) => setInterestRate(+e.target.value)}
              />
            </label>

            <label>
              Monthly insurance: <br />
              <input
                type="text"
                value={`$ ${Number(insurance).toLocaleString()}`}
                onChange={(e) =>
                  setInsurance(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
            </label>

            <label>
              Monthly property tax: <br />
              <input
                type="text"
                value={`$ ${Number(propertyTax).toLocaleString()}`}
                onChange={(e) =>
                  setPropertyTax(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
            </label>

            <label>
              Length of the mortgage: <br />
              <select
                value={loanTerm}
                onChange={(e) => setLoanTerm(+e.target.value)}
              >
                {Array.from({ length: 39 }, (_, i) => (i + 2) * 12).map(
                  (term) => (
                    <option key={term} value={term}>
                      {term} months (or) {term / 12} years
                    </option>
                  )
                )}
              </select>
              <input
                type="range"
                min="24"
                max="480"
                value={loanTerm}
                step="12"
                onChange={(e) => setLoanTerm(+e.target.value)}
              />
            </label>

            <label>
              Homeowner’s Insurance (monthly): <br />
              <input
                type="text"
                value={`$ ${Number(insurance).toLocaleString()}`}
                onChange={(e) =>
                  setInsurance(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
            </label>

            <label>
              Association Fee (monthly): <br />
              <input
                type="text"
                value={`$ ${Number(assocFee).toLocaleString()}`}
                onChange={(e) =>
                  setAssocFee(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
            </label>

            {/* ... all other fields remain unchanged (grossIncome, debt, etc.) */}

            {/* Keep your other inputs here as is */}
          </div>

          <div className="rightSideBox">
            <label className="afford_rightSideTop">
              <span>Home Price: </span>
              <input
                type="text"
                value={`$ ${Number(homePrice.toFixed(2)).toLocaleString(
                  undefined,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`}
                readOnly
              />
            </label>

            <div style={{ textAlign: "center" }}>
              <PieChart width={pieChartWidth} height={pieChartHeight}>
                <Pie
                  data={data}
                  dataKey="value"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  fill="#8884d8"
                  paddingAngle={1}
                >
                  {data.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `$ ${Number(Math.round(value)).toLocaleString()}`,
                    name,
                  ]}
                  cursor={{ fill: "rgba(255, 255, 255, 0.5)" }}
                />
              </PieChart>
            </div>

            <div className="affordrightSideBottom">
              <div>
                <span>Loan Amount: </span>
                <span>
                  $
                  {Number(loanAmount.toFixed(2)).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div>
                <span>Downpayment:</span>
                <span>
                  {" "}
                  $
                  {Number(downPayment.toFixed(2)).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div>
                <span>Monthly Payment:</span>
                <span>
                  {" "}
                  $
                  {Number(monthlyPayment).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            {loanAmount <= 0 && (
              <div className="affordRightSideBottom RACrightSideBottom00">
                <div>
                  <span style={{ color: "red" }}>
                    Based on industry standards, you may not qualify for a home
                    mortgage. You may need to adjust your income or debt
                    obligations.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffordCalculator;
